import React, { useState, useEffect, useRef } from 'react'
import {
  Breadcrumbs,
  Button,
  Checkbox,
  ContextualMenu,
  FileActions,
  ProgressBar,
  Tabs,
  type BreadcrumbItem,
  type FileActionsAction,
} from '@enamelai/design-system'
import { ReactComponent as SelectionCopyIcon } from '@assets/images/Files/selection-copy.svg'
import { ReactComponent as SelectionDeleteIcon } from '@assets/images/Files/selection-delete.svg'
import { ReactComponent as SelectionDicomIcon } from '@assets/images/Files/selection-dicom.svg'
import { ReactComponent as SelectionDownloadIcon } from '@assets/images/Files/selection-download.svg'
import { ReactComponent as SelectionMoveIcon } from '@assets/images/Files/selection-move.svg'
import FileCard from '@components/pages/Files/FileCard/FileCard.component'
import FilesAndLettersEmptyState from '@components/pages/Files/FilesAndLettersEmptyState/FilesAndLettersEmptyState.component'
import {
  buildFileActions,
  buildFolderActions,
  formatFileDate,
  getFileExtension,
  getFileIconClass,
  getFileNameWithoutExtension,
  getFileTypeFromFileName,
  toContextualMenuItems,
  type FileMenuHandlers,
} from '@components/pages/Files/filesAndLetters.util'
import type { MockFileItem } from '@pages/Files/mockFilesData'
import './FilesAndLettersFolderGrid.css'

export type FolderItem = {
  id: string
  name: string
  isDefault: boolean
  isStarred?: boolean
  isSelected?: boolean
  parentId?: string
  path?: string
  /** Shown only in the UI until the user confirms the name and the folder is created on the server. */
  isLocalDraft?: boolean
  /** DB `parent_folder_id` used when persisting a local draft (patient root id for top-level drafts). */
  draftParentDbFolderId?: string | null
  /** Parent folder path when the parent row is not in the loaded workspace/move-modal list (e.g. move modal drill-in). */
  parentPathSnapshot?: string
}

export type MixedRootItem =
  | { type: 'folder'; folder: FolderItem }
  | { type: 'file'; file: MockFileItem }

export interface FilesAndLettersFolderGridProps {
  storageUsed: string
  storageLimit: string
  storagePercent: number
  storageLoading?: boolean
  showStarred: boolean
  onShowStarredChange: (value: boolean) => void
  viewMode: 'grid' | 'list'
  onViewModeChange: (mode: 'grid' | 'list') => void
  defaultFolders: FolderItem[]
  customFolders: FolderItem[]
  onFolderSelect?: (folder: FolderItem) => void
  onCustomFolderSelect?: (folder: FolderItem, selected: boolean) => void
  onFolderDownloadZip?: (folder: FolderItem) => void
  onFolderDetails?: (folder: FolderItem) => void
  onFolderMakeCopy?: (folder: FolderItem) => void
  onFolderRename?: (folder: FolderItem) => void
  onFolderMove?: (folder: FolderItem) => void
  onFolderDelete?: (folder: FolderItem) => void
  openFolder: FolderItem | null
  breadcrumbFolders?: FolderItem[]
  onBreadcrumbRootClick?: () => void
  files: MockFileItem[]
  selectedFileIds: string[]
  onClearSelection?: () => void
  onFileSelect: (fileId: string) => void
  onFileFavorite: (fileId: string) => void
  onFileDelete: (fileId: string) => void
  onFileDownload: (fileId: string) => void
  showDicomInSelectionBar?: boolean
  onFileViewFile?: (fileId: string) => void
  onFileViewWithDicom?: (fileId: string) => void
  onFileViewWithDicomMultiple?: (fileIds: string[]) => void
  onFileNotes?: (fileId: string) => void
  onFileDetails?: (fileId: string) => void
  onFileMakeCopy?: (fileId: string) => void
  onFileRename?: (fileId: string) => void
  onFileMove?: (fileId: string) => void
  onFileMoveMultiple?: (fileIds: string[]) => void
  onMoveFilesAndFolders?: (fileIds: string[], folderIds: string[]) => void
  onFileAttachToClaims?: (fileId: string) => void
  onFileRenameConfirm?: (fileId: string, newName: string) => void
  onFolderRenameConfirm?: (folderId: string, newName: string) => void
  /** Called on Escape during folder rename (e.g. cancel an unsaved draft). If omitted, Escape submits the previous name. */
  onFolderRenameEscape?: (folderId: string) => void
  renamingId?: string | null
  sampleFiles?: MockFileItem[]
  mixedRootItems?: MixedRootItem[] | null
  /** Open the shared Upload modal from empty states. */
  onUploadRequest?: () => void
  /** True when search, type filter, or starred is narrowing the list. */
  isListNarrowed?: boolean
}

type ViewMode = FilesAndLettersFolderGridProps['viewMode']

const DEFAULT_FOLDER_NAMES = [
  'X-Ray Files',
  'Forms and Letters',
  'Claims',
  'Images',
]

const VIEW_MODE_TABS = [
  { id: 'list', label: 'List' },
  { id: 'grid', label: 'Grid' },
]

const stopPropagation = (e: React.SyntheticEvent): void => e.stopPropagation()

function focusAndSelect(el: HTMLInputElement | null): void {
  if (!el) return
  setTimeout(() => {
    el.focus()
    el.select()
  }, 50)
}

function FilesAndLettersFolderGrid({
  storageUsed = '3.8GB',
  storageLimit = '10GB',
  storagePercent = 38,
  storageLoading = false,
  showStarred,
  onShowStarredChange,
  viewMode,
  onViewModeChange,
  defaultFolders = DEFAULT_FOLDER_NAMES.map((name, i) => ({
    id: `default-${i}`,
    name,
    isDefault: true,
  })),
  customFolders = [
    { id: 'custom-1', name: 'Custom folder 1', isDefault: false },
  ],
  onFolderSelect,
  onCustomFolderSelect,
  onFolderDownloadZip,
  onFolderDetails,
  onFolderMakeCopy,
  onFolderRename,
  onFolderMove,
  onFolderDelete,
  openFolder,
  breadcrumbFolders,
  onBreadcrumbRootClick,
  files = [],
  selectedFileIds = [],
  onClearSelection,
  onFileSelect,
  onFileFavorite,
  onFileDelete,
  onFileDownload,
  showDicomInSelectionBar = false,
  onFileViewFile,
  onFileViewWithDicom,
  onFileViewWithDicomMultiple,
  onFileNotes,
  onFileDetails,
  onFileMakeCopy,
  onFileRename,
  onFileMove,
  onFileMoveMultiple,
  onMoveFilesAndFolders,
  onFileAttachToClaims,
  onFileRenameConfirm,
  onFolderRenameConfirm,
  onFolderRenameEscape,
  renamingId = null,
  sampleFiles = [],
  mixedRootItems = null,
  onUploadRequest,
  isListNarrowed = false,
}: FilesAndLettersFolderGridProps): React.ReactElement {
  const showMixedRoot =
    !openFolder && mixedRootItems != null && mixedRootItems.length > 0
  const [isDragOver, setIsDragOver] = useState(false)
  const renameStartRef = useRef(0)

  const handleFileOpen = (fileId: string, fileName: string): void => {
    const fileType = getFileTypeFromFileName(fileName)
    if (fileType === 'dcm' && onFileViewWithDicom) {
      onFileViewWithDicom(fileId)
      return
    }
    onFileViewFile?.(fileId)
  }
  const topLevelCustomFolders = customFolders.filter((f) => !f.parentId)
  const childFolders =
    openFolder != null
      ? customFolders.filter((f) => f.parentId === openFolder.id)
      : []

  const resolvedBreadcrumbFolders: FolderItem[] = React.useMemo(() => {
    if (!openFolder) return []
    if (breadcrumbFolders && breadcrumbFolders.length > 0) {
      return breadcrumbFolders
    }
    const allFolders: FolderItem[] = [...defaultFolders, ...customFolders]
    const path: FolderItem[] = []
    const visited = new Set<string>()
    let current: FolderItem | undefined | null = openFolder

    while (current && !visited.has(current.id)) {
      path.unshift(current)
      visited.add(current.id)
      const parentId: string | undefined = current.parentId
      if (!parentId) break
      current = allFolders.find((f: FolderItem) => f.id === parentId) ?? null
    }

    return path
  }, [openFolder, breadcrumbFolders, defaultFolders, customFolders])

  const breadcrumbItems: BreadcrumbItem[] = openFolder
    ? [
        { id: 'root', label: 'Folders', onClick: onBreadcrumbRootClick },
        ...resolvedBreadcrumbFolders.map((folder) => ({
          id: folder.id,
          label: folder.name,
          onClick: () => onFolderSelect?.(folder),
        })),
      ]
    : [{ id: 'root', label: 'Folders' }]

  const handleBreadcrumbBack = (): void => {
    const parentFolder = resolvedBreadcrumbFolders.at(-2)
    if (parentFolder) {
      onFolderSelect?.(parentFolder)
    } else {
      onBreadcrumbRootClick?.()
    }
  }

  useEffect(() => {
    if (renamingId) {
      renameStartRef.current = Date.now()
    }
  }, [renamingId])

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault()
    setIsDragOver(true)
  }
  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault()
    setIsDragOver(false)
  }
  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault()
    setIsDragOver(false)
    onUploadRequest?.()
  }

  const getFileMenuHandlers = (fileId: string): FileMenuHandlers => ({
    onView: () => onFileViewFile?.(fileId),
    onViewWithDicom: onFileViewWithDicom
      ? () => onFileViewWithDicom(fileId)
      : undefined,
    onDownload: () => onFileDownload(fileId),
    onNotes: () => onFileNotes?.(fileId),
    onDetails: () => onFileDetails?.(fileId),
    onRename: () => onFileRename?.(fileId),
    onMove: () => onFileMove?.(fileId),
    onMakeCopy: () => onFileMakeCopy?.(fileId),
    onAttachToClaims: () => onFileAttachToClaims?.(fileId),
    onDelete: () => onFileDelete(fileId),
  })

  const handleMoveSelected = (): void => {
    const selectedFolderIds = customFolders
      .filter((f) => f.isSelected)
      .map((f) => f.id)
    if (
      onMoveFilesAndFolders &&
      (selectedFileIds.length > 0 || selectedFolderIds.length > 0)
    ) {
      onMoveFilesAndFolders(selectedFileIds, selectedFolderIds)
    } else if (onFileMoveMultiple && selectedFileIds.length > 0) {
      onFileMoveMultiple(selectedFileIds)
    } else if (selectedFileIds[0]) {
      onFileMove?.(selectedFileIds[0])
    }
  }

  const selectionActions: FileActionsAction[] = [
    {
      id: 'download',
      label: 'Download',
      icon: (
        <SelectionDownloadIcon className="fal-selection-icon" aria-hidden />
      ),
      onClick: () => selectedFileIds.forEach((id) => onFileDownload(id)),
    },
    {
      id: 'move',
      label: 'Move',
      icon: <SelectionMoveIcon className="fal-selection-icon" aria-hidden />,
      onClick: handleMoveSelected,
    },
    ...(showDicomInSelectionBar &&
    (onFileViewWithDicomMultiple ?? onFileViewWithDicom)
      ? [
          {
            id: 'dicom',
            label: 'Open with DICOM',
            icon: (
              <SelectionDicomIcon
                className="fal-selection-icon fal-selection-icon--dicom"
                aria-hidden
              />
            ),
            onClick: () => {
              if (onFileViewWithDicomMultiple) {
                onFileViewWithDicomMultiple(selectedFileIds)
              } else {
                selectedFileIds.forEach((id) => onFileViewWithDicom?.(id))
              }
            },
          },
        ]
      : []),
    {
      id: 'copy',
      label: 'Make a copy',
      icon: <SelectionCopyIcon className="fal-selection-icon" aria-hidden />,
      onClick: () => selectedFileIds.forEach((id) => onFileMakeCopy?.(id)),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <SelectionDeleteIcon className="fal-selection-icon" aria-hidden />,
      danger: true,
      onClick: () => selectedFileIds[0] && onFileDelete(selectedFileIds[0]),
    },
  ]

  const renderFolderRenameInput = (
    folder: FolderItem,
    className: string,
  ): React.ReactNode => (
    <input
      ref={focusAndSelect}
      className={className}
      defaultValue={folder.name}
      onClick={stopPropagation}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onFolderRenameConfirm?.(folder.id, e.currentTarget.value)
        } else if (e.key === 'Escape') {
          e.preventDefault()
          if (onFolderRenameEscape) onFolderRenameEscape(folder.id)
          else onFolderRenameConfirm?.(folder.id, folder.name)
        }
      }}
      onBlur={(e) => {
        if (Date.now() - renameStartRef.current < 200) return
        onFolderRenameConfirm?.(folder.id, e.target.value)
      }}
    />
  )

  const renderFolder = (
    folder: FolderItem,
    view: ViewMode,
  ): React.ReactNode => {
    const tone = folder.isDefault ? 'default' : 'custom'
    const isList = view === 'list'
    const menuItems = toContextualMenuItems(
      buildFolderActions({
        onDownloadZip: () => onFolderDownloadZip?.(folder),
        onDetails: () => onFolderDetails?.(folder),
        onMakeCopy: () => onFolderMakeCopy?.(folder),
        onRename: () => onFolderRename?.(folder),
        onMove: () => onFolderMove?.(folder),
        onDelete: () => onFolderDelete?.(folder),
      }),
    )

    return (
      <div
        key={folder.id}
        className={`${
          isList
            ? `fal-list-row fal-list-row-folder fal-list-row-folder-${tone}`
            : `files-and-letters-folder-box files-and-letters-folder-${tone}`
        }${folder.isSelected ? ' fal-folder-selected' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => onFolderSelect?.(folder)}
        onKeyDown={(e) => e.key === 'Enter' && onFolderSelect?.(folder)}
      >
        <div
          className={
            isList ? 'fal-list-row-left' : 'files-and-letters-folder-left'
          }
        >
          {!folder.isDefault && (
            <span className="fal-checkbox" onClick={stopPropagation}>
              <Checkbox
                aria-label={`Select ${folder.name}`}
                checked={!!folder.isSelected}
                onChange={(e) =>
                  onCustomFolderSelect?.(folder, e.target.checked)
                }
              />
            </span>
          )}
          <i
            className={`mgc_folder_fill fal-folder-icon fal-folder-icon-${tone}`}
            aria-hidden
          />
          {renamingId === folder.id ? (
            renderFolderRenameInput(folder, 'fal-rename-input')
          ) : (
            <span className="fal-item-name">{folder.name}</span>
          )}
        </div>
        <div
          className={
            isList ? 'fal-list-row-right' : 'files-and-letters-folder-right'
          }
          onClick={stopPropagation}
          onKeyDown={stopPropagation}
        >
          {isList && (
            <>
              <span className="fal-list-date" />
              <span className="fal-list-size" />
            </>
          )}
          <ContextualMenu
            ariaLabel={`${folder.name} actions`}
            items={menuItems}
            className="fal-item-menu"
          />
        </div>
      </div>
    )
  }

  const renderFileRenameInput = (file: MockFileItem): React.ReactNode => {
    const extension = getFileExtension(file.fileName)
    const commit = (value: string): void => {
      const base = value.trim()
      onFileRenameConfirm?.(
        file.id,
        base !== '' ? base + extension : file.fileName,
      )
    }
    return (
      <span className="fal-list-rename-wrapper">
        <input
          ref={focusAndSelect}
          className="fal-rename-input"
          defaultValue={getFileNameWithoutExtension(file.fileName)}
          onClick={stopPropagation}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commit(e.currentTarget.value)
            } else if (e.key === 'Escape') {
              onFileRenameConfirm?.(file.id, file.fileName)
            }
          }}
          onBlur={(e) => {
            if (Date.now() - renameStartRef.current < 200) return
            commit(e.target.value)
          }}
        />
        {extension ? (
          <span className="fal-list-rename-extension">{extension}</span>
        ) : null}
      </span>
    )
  }

  const renderFileRow = (file: MockFileItem): React.ReactNode => {
    const fileType = getFileTypeFromFileName(file.fileName)
    const isSelected = selectedFileIds.includes(file.id)
    const menuItems = toContextualMenuItems(
      buildFileActions(getFileMenuHandlers(file.id)),
      fileType,
    )

    return (
      <div
        key={file.id}
        className={`fal-list-row${isSelected ? ' fal-list-row-selected' : ''}`}
        role="button"
        tabIndex={0}
        aria-label={file.fileName}
        onClick={() => handleFileOpen(file.id, file.fileName)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleFileOpen(file.id, file.fileName)
          }
        }}
      >
        <div className="fal-list-row-left">
          <span className="fal-checkbox" onClick={stopPropagation}>
            <Checkbox
              aria-label={`Select ${file.fileName}`}
              checked={isSelected}
              onChange={() => onFileSelect(file.id)}
            />
          </span>
          <Button
            variant="ghost"
            size="xs"
            className={`fal-list-star${file.isFavorite ? ' fal-list-star--on' : ''}`}
            aria-label={file.isFavorite ? 'Unfavorite' : 'Favorite'}
            aria-pressed={file.isFavorite}
            onClick={(e) => {
              e.stopPropagation()
              onFileFavorite(file.id)
            }}
          >
            <i
              className={`mgc ${file.isFavorite ? 'mgc_star_fill' : 'mgc_star_line'}`}
              aria-hidden
            />
          </Button>
          <i
            className={`${getFileIconClass(fileType)} fal-list-file-icon fal-list-file-icon-${fileType}`}
            aria-hidden
          />
          {renamingId === file.id ? (
            renderFileRenameInput(file)
          ) : (
            <span className="fal-item-name">{file.fileName}</span>
          )}
        </div>
        <div
          className="fal-list-row-right"
          onClick={stopPropagation}
          onKeyDown={stopPropagation}
        >
          <span className="fal-list-date">
            {formatFileDate(file.uploadDate)}
          </span>
          <span className="fal-list-size">{file.fileSize}</span>
          <ContextualMenu
            ariaLabel={`${file.fileName} actions`}
            items={menuItems}
            className="fal-item-menu"
          />
        </div>
      </div>
    )
  }

  const renderFileCard = (file: MockFileItem): React.ReactNode => (
    <FileCard
      key={file.id}
      fileName={file.fileName}
      fileSize={file.fileSize}
      uploadDate={file.uploadDate}
      isSelected={selectedFileIds.includes(file.id)}
      isFavorite={file.isFavorite}
      onSelect={() => onFileSelect(file.id)}
      onFavorite={() => onFileFavorite(file.id)}
      onRenameConfirm={(newName) => onFileRenameConfirm?.(file.id, newName)}
      isRenaming={renamingId === file.id}
      {...getFileMenuHandlers(file.id)}
    />
  )

  const renderEmptyFolder = (): React.ReactNode => (
    <FilesAndLettersEmptyState
      variant={isListNarrowed ? 'results' : 'emptyFolder'}
      onChooseFile={onUploadRequest}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      isDragOver={isDragOver}
    />
  )

  const rootFolders = [...defaultFolders, ...topLevelCustomFolders]

  return (
    <div className="files-and-letters-main">
      <div className="files-and-letters-top-section">
        <div className="files-and-letters-storage-container">
          <div className="files-and-letters-storage-container-top">
            <span className="mgc_upload_3_fill" aria-hidden />
            <p>Storage used</p>
            <p>
              {storageLoading ? (
                'Loading...'
              ) : (
                <>
                  {storageUsed} <span>/ {storageLimit}</span>{' '}
                  {'(' + Math.round(storagePercent) + '%)'}
                </>
              )}
            </p>
          </div>
          <ProgressBar
            value={storagePercent}
            hideLabel
            aria-label="Storage used"
            className="files-and-letters-storage-progress"
          />
        </div>
        <div className="files-and-letters-top-right">
          <Checkbox
            id="files-and-letters-show-starred"
            label="Show Starred"
            className="files-and-letters-show-starred"
            checked={showStarred}
            onChange={(e) => onShowStarredChange(e.target.checked)}
          />
          <Tabs
            items={VIEW_MODE_TABS}
            value={viewMode}
            onChange={(id) => onViewModeChange(id as ViewMode)}
            className="files-and-letters-view-toggle"
          />
        </div>
      </div>

      <Breadcrumbs
        items={breadcrumbItems}
        showBackIcon={openFolder != null}
        onBackClick={handleBreadcrumbBack}
        className="files-and-letters-breadcrumbs"
      />

      {selectedFileIds.length >= 1 && (
        <FileActions
          selectedCount={selectedFileIds.length}
          onClear={onClearSelection}
          actions={selectionActions}
          className="files-and-letters-selection-bar"
        />
      )}

      {openFolder && (
        <div className="files-and-letters-content">
          {childFolders.length > 0 && (
            <div
              className={
                viewMode === 'grid'
                  ? 'files-and-letters-folders-group'
                  : 'fal-list-view'
              }
            >
              {childFolders.map((folder) => renderFolder(folder, viewMode))}
            </div>
          )}
          {files.length === 0 && childFolders.length === 0 ? (
            renderEmptyFolder()
          ) : viewMode === 'grid' ? (
            <div className="files-and-letters-file-grid">
              {files.map(renderFileCard)}
            </div>
          ) : (
            <div className="fal-list-view">{files.map(renderFileRow)}</div>
          )}
        </div>
      )}

      {showMixedRoot && mixedRootItems && (
        <div
          className={
            viewMode === 'grid'
              ? 'files-and-letters-file-grid files-and-letters-mixed-root-grid'
              : 'fal-list-view'
          }
        >
          {mixedRootItems.map((item) => {
            if (item.type === 'folder')
              return renderFolder(item.folder, viewMode)
            return viewMode === 'grid'
              ? renderFileCard(item.file)
              : renderFileRow(item.file)
          })}
        </div>
      )}

      {!openFolder && !showMixedRoot && (
        <div
          className={
            viewMode === 'grid'
              ? 'files-and-letters-folders-group'
              : 'fal-list-view'
          }
        >
          {rootFolders.map((folder) => renderFolder(folder, viewMode))}
        </div>
      )}

      {!openFolder && !showMixedRoot && sampleFiles.length > 0 && (
        <div className="files-and-letters-sample-section">
          {viewMode === 'grid' ? (
            <div className="files-and-letters-file-grid">
              {sampleFiles.map(renderFileCard)}
            </div>
          ) : (
            <div className="fal-list-view">
              {sampleFiles.map(renderFileRow)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default FilesAndLettersFolderGrid
